import $ from '../service/container';
import {ApolloClient} from 'apollo-client'
import {createHttpLink} from 'apollo-link-http'
import {InMemoryCache} from 'apollo-cache-inmemory'
import {setContext} from '@apollo/client/link/context'
import { ApolloLink } from "apollo-link"
import { onError } from 'apollo-link-error';
import version from '../version';

import { AuthenticationManager } from "./manager/AuthenticationManager";
import VueApollo from 'vue-apollo'

import store from './store'
import router from './router'
import ApiRouter from "../manager/ApiRouter"
import RequestLoader from "../manager/RequestLoader"
import {UserManager} from "../manager/UserManager"
import "../manager/scrollDetects"
import {PatientManager} from "../manager/PatientManager";
import {KnowledgeManager} from "../manager/KnowledgeManager";
import registerAuthenticationEvents from "../event/authentication";
import registerPatientEvents from "../event/patient";
import {TranslationManager} from "../manager/TranslationManager";
import {BehaviourComponentManager} from "../manager/BehaviourComponentManager";
import { QuestionnaireManager} from "./manager/QuestionnaireManager";
import {ActivityManager} from "../manager/ActivityManager";
import {ScheduleManager} from "../manager/ScheduleManager";
import {TimeManager} from "../manager/TimeManager";
import Vue from 'vue';
import {RouteRequirementsManager} from "./manager/RouteRequirementsManager";
import {HealthyNutritionManager} from "../manager/HealthyNutritionManager";
import ApexChartHelper from "../manager/ApexChartHelper";
import {ContentManager} from "dashboard/manager/ContentManager";
import {ReportManager} from "../manager/ReportManager";

const hasDebugCookie = document.cookie.match(/^(.*;)?\s*debug\s*=\s*[^;]+(.*)?$/);

/**
 *
 * @name main.app
 * @prop appType Type of application
 * @prop router Vue Router
 * @prop env
 * @prop version
 * @prop eventBus
 * @prop apiRouter
 * @prop apolloClient API handling
 * @prop hasDSSDebugCookie Debug
 * @prop apolloMobileClient API handling
 * @prop apolloProvider API handling
 * @prop authentication new {@link AuthenticationManager}
 * @prop user new {@link UserManager}
 * @prop timeManager new {@link TimeManager}
 * @prop patientManager new {@link PatientManager}
 * @prop behaviourComponentManager new {@link BehaviourComponentManager}
 * @prop translationManager new {@link TranslationManager}
 * @prop acrivityManager new {@link ActivityManager}
 * @prop scheduleManager new {@link ScheduleManager}
 * @prop knowledge new {@link KnowledgeManager}
 * @prop questionnaireManager new {@link QuestionnaireManager}
 * @prop store Vuex
 * @prop requestLoader
 * @prop registeredEvents Events
 */

const app = {
  appType: () => {
    return 'dashboard'
  },

  router: () => {
    return router;
  },

  env: () => {
    return process.env
  },

  version: () => {
    return version;
  },

  eventBus: () => {
    return new Vue();
  },

  apiRouter: () => {
    const apiRouter = new ApiRouter(process.env.VUE_APP_API_ROOT_URL)

    if (process.env.VUE_APP_USE_XDEBUG == "1") apiRouter.addDefaultParameter('XDEBUG_SESSION_START', 'PHPSTORM')

    apiRouter.addRoute('login', function(parameters){
      return '/api/login_check_sso'
    })

    apiRouter.addRoute('refreshToken', function(parameters){
      return '/api/token/refresh'
    })

    apiRouter.addRoute('requestPasswordReset', function(parameters){
      return '/api/request_password_reset'
    })

    apiRouter.addRoute('resetPassword', function(parameters){
      return '/api/password_reset'
    })

    apiRouter.addRoute('dashboardGraphQL', function(parameters){
      return '/graphql/multi/dashboard'
    })

    apiRouter.addRoute('mobileGraphQL', function(parameters){
      return '/graphql/multi/mobile'
    })

    apiRouter.addRoute('retrieveReports', function(parameters){
      return '/app/reports'
    })

    return apiRouter
  },

  apolloClient: () => {
    const uri = app.apiRouter.getRoute('dashboardGraphQL');

    const httpLink = createHttpLink({
      uri: uri,
      credentials: 'include'
    })

    const cache = new InMemoryCache()

    const headerLink = setContext((_, {headers}) => {
      const token = app.store.state.authToken;

      return {
        headers: {
          ...headers,
          authorization: token ? `Bearer ${token}` : "",
          'Accept-Language' : app.translationManager.getCurrentLanguage(),
          'current-app' : 'dashboard',
          'current-version' : version,
          'current-screen' : app.router.currentRoute ? app.router.currentRoute.name : null,
          'timezone' : app.timeManager.getClientTimezone().name,
        }
      }
    });

    const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
      if (graphQLErrors)
        graphQLErrors.forEach((error) => app.store.dispatch('error/graphqlSubmit', {error, operation, uri}, {root : true}))
    });

    const debugLink = new ApolloLink((operation, forward) => {
      if (hasDebugCookie)
        app.store.dispatch('error/graphqlDebug', {operation, uri}, {root : true});

      return forward(operation);
    });

    return new ApolloClient({
      link: ApolloLink.from([
        headerLink,
        errorLink,
        debugLink,
        app.authentication.createRefreshLink(),
        httpLink,
      ]),
      cache,
      defaultOptions : {
        watchQuery: {
          fetchPolicy: 'no-cache',
          errorPolicy: 'ignore',
        },
        query: {
          fetchPolicy: 'no-cache',
          errorPolicy: 'all',
        },
      }
    })
  },

  hasDSSDebugCookie : () =>  !!document.cookie.match(/^(.*;)?\s*DSSDebug\s*=\s*[^;]+(.*)?$/),

  apolloMobileClient: () => {
    const uri = app.apiRouter.getRoute('mobileGraphQL');

    const httpLink = createHttpLink({
      uri: uri,
      credentials: 'include'
    })

    const cache = new InMemoryCache()

    const headerLink = setContext((_, {headers}) => {
      const token = app.store.state.authToken;

      return {
        headers: {
          ...headers,
          authorization: token ? `Bearer ${token}` : "",
          'Accept-Language' : app.translationManager.getCurrentLanguage(),
          'current-app' : 'dashboard',
          'current-screen' : app.router.currentRoute ? app.router.currentRoute.name : null,
          'timezone' : app.timeManager.getClientTimezone().name,
        }
      }
    });

    const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
      if (graphQLErrors)
        graphQLErrors.forEach((error) => app.store.dispatch('error/graphqlSubmit', {error, operation, uri}, {root : true}))
    });

    const debugLink = new ApolloLink((operation, forward) => {
      if (hasDebugCookie)
        app.store.dispatch('error/graphqlDebug', {operation, uri}, {root : true});

      return forward(operation);
    });

    return new ApolloClient({
      link: ApolloLink.from([
        headerLink,
        errorLink,
        debugLink,
        app.authentication.createRefreshLink(),
        httpLink,
      ]),
      cache,
      defaultOptions : {
        watchQuery: {
          fetchPolicy: 'no-cache',
          errorPolicy: 'ignore',
        },
        query: {
          fetchPolicy: 'no-cache',
          errorPolicy: 'all',
        },
      }
    })
  },

  apolloProvider : () => {
    return new VueApollo({
      defaultClient: app.apolloClient,
      clients: {
        dashboardClient: app.apolloClient,
        mobileClient: app.apolloMobileClient
      }
    })
  },

  authentication: () => {
    return new AuthenticationManager(app.store, app.apiRouter, app.router, version)
  },

  user: () => {
    return new UserManager(app.authentication)
  },

  timeManager: () => {
    return new TimeManager();
  },

  patientManager: () => {
    return new PatientManager(app.apolloClient, app.apolloMobileClient, app.questionnaireManager, app.store, app.authentication)
  },

  behaviourComponentManager: () => {
    return new BehaviourComponentManager(app.apolloMobileClient, app.translationManager)
  },

  translationManager: () => {
    const translationManager = new TranslationManager(
        app.store,
        () => app.authentication.getToken()
    )

    const baseUrl = process.env.VUE_APP_LOCAL_URL_DASHBOARD + '/translations/';
    const apiUrl = process.env.VUE_APP_API_ROOT_URL + '/app/translations/';

    translationManager
        // The local catalog is a special catalog, it contains a few translations that are not loaded from back-end
        // All back-end translations are behind user login, a few screens such as the login-screen need translations
        // which can't be loaded from the back-end and need to be loaded 'locally'
        .defineCatalog('local', [baseUrl + 'local'])
        .defineCatalog('app_dashboard', [apiUrl + 'app_dashboard'])
        .defineCatalog('app_tablet', [apiUrl + 'app_tablet'])
        .defineCatalog('app_mobile', [apiUrl + 'app_mobile'])
        .defineCatalog('questionnaires', [apiUrl + 'questionnaires'])
        .defineCatalog('content', [apiUrl + 'content'])
        .defineCatalog('validators', [apiUrl + 'validators'])
        .defineCatalog('messages', [apiUrl + 'messages'])
        .defineCatalog('answersets', [apiUrl + 'answersets'])
        .defaultCatalog('app_dashboard')
    ;

    return translationManager
  },

  apexChartHelper: () => {
    return new ApexChartHelper(app.store, app.translationManager);
  },

  activityManager: () => {
    return new ActivityManager(app.translationManager)
  },

  scheduleManager: () => {
    return new ScheduleManager(app.translationManager)
  },

  knowledgeManager: () => {
    return new KnowledgeManager(app.apolloMobileClient, app.store)
  },

  questionnaireManager: () => {
    return new QuestionnaireManager(app.apolloClient, app.apolloMobileClient, app.store)
  },

  contentManager: () => {
    return new ContentManager(app.apolloMobileClient)
  },

  healthyNutritionManager: () => {
    return new HealthyNutritionManager(app.apolloMobileClient)
  },

  reportManager: () => {
    return new ReportManager(app.apiRouter, app.store, app.authentication)
  },

  store: () => {
    return store
  },

  routeRequirements: () => {
    return new RouteRequirementsManager(app.store, app.router)
  },

  requestLoader: {
    mode: $.MODE_EAGER,
    invokable: () => {
      return new RequestLoader(app.store, true)
    }
  },

  //EVENTS
  registeredEvents: {
    mode: $.MODE_EAGER,
    invokable: () => {
      registerAuthenticationEvents(app.store)
      registerPatientEvents(app.store, app.patientManager)
    }
  },
};

export function mapRouteRequirements(routeRequirements) {
  const ret = {};

  for (let key in routeRequirements) {
    ret[key] = () =>  app.routeRequirements.isAllowed(routeRequirements[key])
  }

  return ret;
}

export default app;
